import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  username: string | null;
  username_changes_today: number;
  last_username_change_date: string | null;
  plan: 'free' | 'ghost' | 'phantom' | 'elite';
  credits_used: number;
  credits_limit: number;
  tokens_balance: number;
  tokens_used: number;
  token_multiplier: number;
  has_unlimited_tokens: boolean;
  is_banned: boolean;
  created_at: string;
  updated_at: string;
  // Learning fields
  total_xp: number | null;
  current_rank: string | null;
  rank_progress: number | null;
}

interface UserRole {
  role: 'admin' | 'user';
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: UserRole[];
  isAdmin: boolean;
  loading: boolean;
  creditsRemaining: number;
  hasCredits: boolean;
  tokensBalance: number;
  hasTokens: boolean;
  hasUnlimitedTokens: boolean;
  refreshProfile: () => Promise<void>;
  patchProfile: (updates: Partial<Profile>) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data: profileData } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    
    if (profileData) {
      setProfile(profileData as Profile);
    }

    const { data: rolesData } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);
    
    if (rolesData) {
      setRoles(rolesData as UserRole[]);
    }
  };

  const patchProfile = (updates: Partial<Profile>) => {
    setProfile((prev) => (prev ? ({ ...prev, ...updates } as Profile) : prev));
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  const signOut = async () => {
    try {
      // Clear local state first to ensure immediate UI update
      setUser(null);
      setSession(null);
      setProfile(null);
      setRoles([]);
      // Then call Supabase signOut
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.error('Sign out error:', error);
      // State already cleared, so user is effectively logged out locally
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setTimeout(() => {
          fetchProfile(session.user.id);
        }, 0);
      } else {
        setProfile(null);
        setRoles([]);
      }
      
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Subscribe to profile changes for real-time token updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`profile_updates_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          if (payload.new) {
            setProfile(payload.new as Profile);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const isAdmin = roles.some(r => r.role === 'admin');
  const creditsRemaining = profile ? profile.credits_limit - profile.credits_used : 0;
  const hasCredits = creditsRemaining > 0 && !profile?.is_banned;
  const tokensBalance = profile?.tokens_balance ?? 0;
  const hasUnlimitedTokens = profile?.has_unlimited_tokens ?? false;
  const hasTokens = hasUnlimitedTokens || (tokensBalance > 0 && !profile?.is_banned);

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      roles,
      isAdmin,
      loading,
      creditsRemaining,
      hasCredits,
      tokensBalance,
      hasTokens,
      hasUnlimitedTokens,
      refreshProfile,
      patchProfile,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
